import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import BrandHeader from "../components/BrandHeader";
import BrandFooter from "../components/BrandFooter";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  UploadCloud, FileArchive, Loader2, CheckCircle2, AlertTriangle, KeyRound,
  Building2, MapPin, Users, HardDrive, Globe, FileText, Layers, Trash2,
  ShieldAlert, PlayCircle, Zap, Paperclip, Puzzle, ArrowRight, LinkIcon,
  UserPlus,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

/**
 * ITGlue import wizard — 3 phases, one page:
 *
 *   1. UPLOAD:   dropzone accepts a .zip → POSTs multipart to /import/itglue/upload
 *                → backend extracts + parses → returns { import_id, preview }
 *   2. PREVIEW:  full breakdown of what will be imported. Two commit buttons:
 *                Dry-run (safe validation) + Full push (writes to live ITGlue).
 *   3. PROGRESS: live poll of /import/itglue/{id}/status every 1.5s → shows
 *                stage, current/total, message. Final panel: totals + log tail.
 *
 * Passwords are surfaced with a rose warning banner in the preview since the
 * export we tested against contains 127 plaintext credentials.
 */
export default function AdminImportITGlue() {
  const nav = useNavigate();
  const [phase, setPhase] = useState("upload"); // upload | preview | progress
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [record, setRecord] = useState(null); // import doc from server
  const [progress, setProgress] = useState(null);
  const [confirmPush, setConfirmPush] = useState(null); // 'dry' | 'live' | null
  const [pastImports, setPastImports] = useState([]);
  // Phase 2 state
  const [flexMappingOpen, setFlexMappingOpen] = useState(false);
  const [flexMapping, setFlexMapping] = useState(null);
  const [flexOverrides, setFlexOverrides] = useState({}); // {kind: {csv_col: field_key|null}}
  const [autoCreateTypes, setAutoCreateTypes] = useState(false);
  const [phase2Dry, setPhase2Dry] = useState(true);
  const [reifying, setReifying] = useState(false);
  const fileInput = useRef(null);
  const dropRef = useRef(null);
  const pollRef = useRef(null);

  // Load past imports so admin can pick up an in-flight one after a page reload
  useEffect(() => { loadPast(); }, []);
  const loadPast = async () => {
    try {
      const { data } = await api.get("/import/itglue");
      setPastImports(data.items || []);
    } catch { /* silent */ }
  };

  // ----- Upload flow -----
  const onFiles = async (files) => {
    if (!files?.length) return;
    const f = files[0];
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("Please upload a .zip file");
      return;
    }
    setUploading(true);
    setUploadPct(0);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/import/itglue/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadPct(Math.round(100 * evt.loaded / evt.total));
        },
      });
      setRecord(data.import);
      setPhase("preview");
      toast.success(`Parsed ${data.import.org_name} — ${data.import.preview.core.contacts.count} contacts, ${data.import.preview.core.configurations.count} configurations`);
      loadPast();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  // Drag & drop
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const prevent = (e) => { e.preventDefault(); e.stopPropagation(); };
    const highlight = (e) => { prevent(e); el.classList.add("border-brand-primary", "bg-brand-primary/5"); };
    const unhighlight = (e) => { prevent(e); el.classList.remove("border-brand-primary", "bg-brand-primary/5"); };
    const drop = (e) => { unhighlight(e); onFiles(e.dataTransfer?.files); };
    ["dragenter","dragover"].forEach(n => el.addEventListener(n, highlight));
    ["dragleave","drop"].forEach(n => el.addEventListener(n, unhighlight));
    el.addEventListener("drop", drop);
    return () => {
      ["dragenter","dragover"].forEach(n => el.removeEventListener(n, highlight));
      ["dragleave","drop"].forEach(n => el.removeEventListener(n, unhighlight));
      el.removeEventListener("drop", drop);
    };
  }, [phase]);

  // ----- Commit flow -----
  const doCommit = async (dry_run) => {
    if (!record) return;
    try {
      await api.post(`/import/itglue/${record.id}/commit`, { dry_run });
      setPhase("progress");
      setProgress({ stage: "starting", current: 0, total: 0, message: "Kicking off…" });
      toast.info(dry_run ? "Dry-run started — no writes to ITGlue" : "Live push started");
      // Start polling
      pollStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start push");
    } finally {
      setConfirmPush(null);
    }
  };

  const pollStatus = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      if (!record) return;
      try {
        const { data } = await api.get(`/import/itglue/${record.id}/status`);
        setProgress(data.progress);
        if (data.status === "complete" || data.status === "error") {
          clearInterval(pollRef.current);
          pollRef.current = null;
          const { data: full } = await api.get(`/import/itglue/${record.id}`);
          setRecord(full.import);
          if (data.status === "error") {
            toast.error("Import failed — see log tail");
          } else {
            const totals = full.import.result?.totals || {};
            toast.success(`Import ${full.import.result?.dry_run ? "dry-run " : ""}complete — ${totals.created} created, ${totals.updated} updated${totals.error ? `, ${totals.error} errors` : ""}`);
            loadPast();
          }
        }
      } catch (e) { /* keep polling; probably transient */ }
    }, 1500);
  }, [record]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  const reset = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    setRecord(null); setPhase("upload"); setProgress(null); setUploadPct(0);
  };

  const deleteImport = async (id) => {
    try {
      await api.delete(`/import/itglue/${id}`);
      toast.success("Import removed");
      loadPast();
      if (record?.id === id) reset();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  // ---- Phase 2 handlers ----
  const openFlexMappingModal = async () => {
    if (!record) return;
    try {
      toast.info("Fetching flex-asset schema from target ITGlue…");
      const { data } = await api.get(`/import/itglue/${record.id}/flex-mapping`);
      setFlexMapping(data.flex_mapping);
      // Default auto-create to ON if EVERY type is missing (fresh target tenant)
      const anyExisting = (data.flex_mapping.types || []).some(t => t.exists_on_target);
      setAutoCreateTypes(!anyExisting);
      setFlexOverrides({});
      setFlexMappingOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load flex mapping");
    }
  };

  const commitPhase2 = async () => {
    if (!record) return;
    try {
      await api.post(`/import/itglue/${record.id}/commit-phase2`, {
        dry_run: phase2Dry,
        auto_create_flex_types: autoCreateTypes,
        flex_overrides: flexOverrides,
      });
      setFlexMappingOpen(false);
      setProgress({ stage: "starting", current: 0, total: 0, message: "Starting phase 2…" });
      toast.info(phase2Dry ? "Phase 2 dry-run started" : "Phase 2 live push started");
      pollStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start Phase 2");
    }
  };

  const doReify = async () => {
    if (!record) return;
    setReifying(true);
    try {
      const { data } = await api.post(`/import/itglue/${record.id}/reify-onboarding`);
      toast.success(`Onboarding created for ${data.org_name}`);
      // refetch record to surface reified_onboarding_id
      const { data: full } = await api.get(`/import/itglue/${record.id}`);
      setRecord(full.import);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reify failed");
    } finally {
      setReifying(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="itg-import-page">
      <BrandHeader title="Import from ITGlue" showAdmin showClients showAuditLog showSecurityEvents />
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        <button onClick={() => nav(-1)} className="text-xs text-slate-500 hover:text-slate-700">← Back</button>

        {/* ---- Phase: UPLOAD ---- */}
        {phase === "upload" && (
          <>
            <Card className="p-8" data-testid="itg-upload-card">
              <div
                ref={dropRef}
                onClick={() => !uploading && fileInput.current?.click()}
                className={`border-2 border-dashed border-slate-300 rounded-xl p-12 text-center cursor-pointer transition ${uploading ? "opacity-60 pointer-events-none" : "hover:border-brand-primary hover:bg-brand-primary/5"}`}
                data-testid="itg-dropzone"
              >
                <input
                  ref={fileInput}
                  type="file"
                  accept=".zip"
                  className="hidden"
                  onChange={(e) => onFiles(e.target.files)}
                  data-testid="itg-file-input"
                />
                {uploading ? (
                  <div className="space-y-3">
                    <Loader2 className="mx-auto animate-spin text-brand-primary" size={40} />
                    <div className="text-sm text-slate-600">Uploading + parsing…</div>
                    <div className="max-w-md mx-auto bg-slate-200 rounded-full h-2 overflow-hidden">
                      <div className="bg-brand-primary h-full transition-all" style={{ width: `${uploadPct}%` }} />
                    </div>
                    <div className="text-xs text-slate-500">{uploadPct}%</div>
                  </div>
                ) : (
                  <>
                    <UploadCloud className="mx-auto text-brand-primary mb-3" size={48} />
                    <div className="text-lg font-semibold text-slate-800">Drop an ITGlue export ZIP here</div>
                    <div className="text-sm text-slate-600 mt-1">or click to browse</div>
                    <div className="text-xs text-slate-500 mt-4">
                      Expects the official ITGlue backup format: per-record-type CSVs + <code>attachments/</code> folder.
                      Max 500 MB.
                    </div>
                  </>
                )}
              </div>
            </Card>

            {/* Past imports */}
            {pastImports.length > 0 && (
              <Card className="p-4" data-testid="itg-past-imports">
                <div className="text-sm font-semibold text-slate-800 mb-2">Recent imports</div>
                <div className="space-y-2">
                  {pastImports.map((imp) => (
                    <div key={imp.id} className="flex items-center justify-between bg-slate-50 rounded p-2 text-xs" data-testid={`itg-past-row-${imp.id}`}>
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <FileArchive className="text-slate-400" size={14} />
                        <span className="font-mono text-slate-700 truncate">{imp.filename}</span>
                        <span className="text-slate-500">·</span>
                        <span className="text-slate-700">{imp.org_name}</span>
                        <span className="text-slate-500">·</span>
                        <span className="text-slate-500">{new Date(imp.uploaded_at).toLocaleString()}</span>
                        <span className="text-slate-500">·</span>
                        <StatusPill status={imp.status} />
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-brand-primary"
                          onClick={async () => {
                            const { data } = await api.get(`/import/itglue/${imp.id}`);
                            setRecord(data.import);
                            // Completed imports go to progress panel so Phase 2 + Reify panels render.
                            // In-flight imports also go to progress. Only 'previewed' (never committed) goes to preview.
                            if (data.import.status === "previewed") { setPhase("preview"); }
                            else {
                              setPhase("progress");
                              if (data.import.status === "committing") pollStatus();
                            }
                          }}
                          data-testid={`itg-past-open-${imp.id}`}
                        >Open</Button>
                        <Button size="sm" variant="ghost" className="h-6 px-2 text-rose-600"
                          onClick={() => deleteImport(imp.id)}
                          disabled={imp.status === "committing"}
                          data-testid={`itg-past-delete-${imp.id}`}
                        ><Trash2 size={12}/></Button>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </>
        )}

        {/* ---- Phase: PREVIEW ---- */}
        {phase === "preview" && record && (
          <PreviewPanel
            record={record}
            onDryRun={() => setConfirmPush("dry")}
            onLivePush={() => setConfirmPush("live")}
            onReset={reset}
            onDelete={() => deleteImport(record.id)}
          />
        )}

        {/* ---- Phase: PROGRESS ---- */}
        {phase === "progress" && record && (
          <ProgressPanel
            record={record}
            progress={progress}
            onReset={reset}
            onOpenFlexMapping={openFlexMappingModal}
            onReify={doReify}
            reifying={reifying}
          />
        )}
      </div>

      <BrandFooter />

      {/* Confirm dialog */}
      <AlertDialog open={!!confirmPush} onOpenChange={(v) => !v && setConfirmPush(null)}>
        <AlertDialogContent data-testid="itg-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              {confirmPush === "live" ? (
                <><ShieldAlert className="text-rose-600" size={18}/>Push to live ITGlue?</>
              ) : (
                <><PlayCircle className="text-brand-primary" size={18}/>Run dry-run validation?</>
              )}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmPush === "live" ? (
                <>This will <span className="font-semibold text-rose-700">write to your live ITGlue tenant</span>: <span className="font-mono text-xs">{record?.result?.org_name || record?.org_name}</span>.
                <span className="block mt-2">Records that don&apos;t yet exist are POSTed; matches by name are PATCHed. Archived items are skipped.</span>
                {record?.preview?.core?.passwords?.count > 0 && (
                  <span className="block mt-2 text-rose-700">⚠ {record.preview.core.passwords.count} plaintext password(s) will be sent to ITGlue.</span>
                )}</>
              ) : (
                <>Simulates the push without hitting ITGlue&apos;s write APIs. Confirms every record can be POST&apos;d and reports the counts. Recommended before a live push.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="itg-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => doCommit(confirmPush === "dry")}
              className={confirmPush === "live" ? "bg-rose-600 hover:bg-rose-700" : "bg-brand-primary hover:bg-brand-primary/90"}
              data-testid="itg-confirm-ok"
            >
              {confirmPush === "live" ? "Push to ITGlue" : "Run dry-run"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Flex-asset mapping modal — opens from the Phase 2 continuation card */}
      <Dialog open={flexMappingOpen} onOpenChange={(v) => !v && setFlexMappingOpen(false)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="itg-flex-mapping-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Puzzle className="text-brand-primary" size={18}/>Flex-asset mapping · Phase 2
            </DialogTitle>
            <DialogDescription>
              Each ITGlue export flex-asset type is matched against your target tenant. Missing types can be auto-created; ambiguous columns can be overridden manually.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto space-y-3 py-2 pr-1">
            {flexMapping ? (
              <>
                <div className="text-xs text-slate-500">
                  Target tenant has <span className="font-semibold text-slate-800">{flexMapping.target_types_count}</span> flexible-asset type(s) configured.
                </div>
                {(flexMapping.types || []).map((t) => (
                  <FlexTypeCard
                    key={t.kind}
                    entry={t}
                    overrides={flexOverrides[t.kind] || {}}
                    onOverrideChange={(col, fieldKey) => {
                      setFlexOverrides((old) => {
                        const nx = { ...old, [t.kind]: { ...(old[t.kind] || {}), [col]: fieldKey } };
                        return nx;
                      });
                    }}
                  />
                ))}
              </>
            ) : (
              <div className="py-8 flex items-center justify-center text-slate-500 text-sm">
                <Loader2 className="animate-spin mr-2" size={14}/>Loading…
              </div>
            )}
          </div>

          {flexMapping && (
            <div className="border-t border-slate-200 pt-3 space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <Checkbox
                  id="auto-create"
                  checked={autoCreateTypes}
                  onCheckedChange={(v) => setAutoCreateTypes(!!v)}
                  data-testid="itg-autocreate-checkbox"
                />
                <label htmlFor="auto-create" className="text-slate-700 cursor-pointer">
                  Auto-create missing flex-asset types on target
                </label>
                <span className="text-xs text-slate-500">
                  ({(flexMapping.types || []).filter(t => !t.exists_on_target).length} missing)
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Checkbox
                  id="phase2-dry"
                  checked={phase2Dry}
                  onCheckedChange={(v) => setPhase2Dry(!!v)}
                  data-testid="itg-phase2-dry-checkbox"
                />
                <label htmlFor="phase2-dry" className="text-slate-700 cursor-pointer">
                  Dry-run (recommended — no writes)
                </label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setFlexMappingOpen(false)} data-testid="itg-flex-cancel-btn">Cancel</Button>
            <Button
              className={phase2Dry ? "bg-brand-primary hover:bg-brand-primary/90" : "bg-rose-600 hover:bg-rose-700"}
              onClick={commitPhase2}
              disabled={!flexMapping}
              data-testid="itg-flex-commit-btn"
            >
              {phase2Dry ? <PlayCircle size={14} className="mr-1.5"/> : <Zap size={14} className="mr-1.5"/>}
              {phase2Dry ? "Run Phase 2 dry-run" : "Push Phase 2 live"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ Sub-components =============

function StatusPill({ status }) {
  const map = {
    previewed:   { c: "bg-slate-100 text-slate-700", t: "Previewed" },
    committing:  { c: "bg-sky-100 text-sky-800", t: "Running" },
    complete:    { c: "bg-emerald-100 text-emerald-800", t: "Complete" },
    error:       { c: "bg-rose-100 text-rose-800", t: "Error" },
  };
  const m = map[status] || { c: "bg-slate-100 text-slate-600", t: status || "—" };
  return <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded ${m.c}`}>{m.t}</span>;
}

function PreviewPanel({ record, onDryRun, onLivePush, onReset, onDelete }) {
  const p = record.preview;
  const core = p?.core || {};
  return (
    <div className="space-y-4" data-testid="itg-preview-panel">
      <Card className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-xs text-slate-500 uppercase tracking-wide">Ready to import</div>
            <div className="text-2xl font-bold text-slate-800 mt-0.5" data-testid="itg-preview-org">
              {record.org_name}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              {record.filename} · {(record.size_bytes / 1024 / 1024).toFixed(1)} MB · uploaded {new Date(record.uploaded_at).toLocaleString()}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={onReset} data-testid="itg-upload-another">Upload another</Button>
            <Button variant="ghost" size="sm" className="text-rose-600" onClick={onDelete} data-testid="itg-delete-current">
              <Trash2 size={14}/>
            </Button>
          </div>
        </div>
      </Card>

      {/* Warning banner for plaintext passwords */}
      {core.passwords?.count > 0 && (
        <Card className="p-4 bg-rose-50 border-rose-200" data-testid="itg-password-warning">
          <div className="flex items-start gap-3">
            <KeyRound className="text-rose-600 shrink-0 mt-0.5" size={18}/>
            <div className="text-sm text-rose-900">
              <div className="font-semibold">Plaintext passwords detected — {core.passwords.count} credential(s) will be pushed to ITGlue verbatim.</div>
              <div className="text-xs text-rose-700 mt-1">ITGlue encrypts server-side, but the payload leaves this pod in plaintext over TLS. Confirm the target tenant is the correct destination before running the live push.</div>
            </div>
          </div>
        </Card>
      )}

      {/* Core record cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <CoreCard icon={Building2} label="Organization" count={core.organizations?.count} tint="violet" testid="itg-count-organizations"/>
        <CoreCard icon={MapPin} label="Locations" count={core.locations?.count} tint="sky" testid="itg-count-locations"/>
        <CoreCard icon={Users} label="Contacts" count={core.contacts?.count} sub={`${core.contacts?.active || 0} active`} tint="emerald" testid="itg-count-contacts"/>
        <CoreCard icon={HardDrive} label="Configurations" count={core.configurations?.count} sub={`${core.configurations?.active || 0} active · ${core.configurations?.archived || 0} archived`} tint="amber" testid="itg-count-configurations"/>
        <CoreCard icon={KeyRound} label="Passwords" count={core.passwords?.count} sub={`${core.passwords?.active || 0} active`} tint="rose" testid="itg-count-passwords"/>
        <CoreCard icon={Globe} label="Domains" count={core.domains?.count} tint="slate" testid="itg-count-domains"/>
      </div>

      {/* Config type breakdown */}
      {core.configurations?.by_type && Object.keys(core.configurations.by_type).length > 0 && (
        <Card className="p-4" data-testid="itg-configtypes-panel">
          <div className="text-xs font-semibold uppercase text-slate-600 mb-2">Configurations by type</div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(core.configurations.by_type).map(([type, n]) => (
              <span key={type} className="text-xs bg-slate-100 border border-slate-200 rounded px-2 py-0.5 text-slate-700">
                <span className="font-medium">{type}</span> <span className="text-slate-500">·</span> {n}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* Documents & attachments — deferred to Phase 2 */}
      <Card className="p-4" data-testid="itg-deferred-panel">
        <div className="text-xs font-semibold uppercase text-slate-600 mb-2">Deferred to a second import pass</div>
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div className="bg-slate-50 rounded p-2">
            <div className="flex items-center gap-1.5 text-slate-700"><FileText size={14}/>Documents</div>
            <div className="text-2xl font-bold text-slate-800 mt-1">{core.documents?.count || 0}</div>
            <div className="text-[11px] text-slate-500">{core.documents?.with_html || 0} with body · {core.documents?.total_attachments || 0} attachments</div>
          </div>
          <div className="bg-slate-50 rounded p-2">
            <div className="flex items-center gap-1.5 text-slate-700"><Layers size={14}/>Flexible Assets</div>
            <div className="text-2xl font-bold text-slate-800 mt-1">{Object.values(p?.flex_assets || {}).reduce((s, v) => s + (v.count || 0), 0)}</div>
            <div className="text-[11px] text-slate-500">across {Object.keys(p?.flex_assets || {}).length} types</div>
          </div>
          <div className="bg-slate-50 rounded p-2">
            <div className="flex items-center gap-1.5 text-slate-700"><FileArchive size={14}/>Attachments</div>
            <div className="text-2xl font-bold text-slate-800 mt-1">{p?.attachments_total || 0}</div>
            <div className="text-[11px] text-slate-500">files linked to documents</div>
          </div>
        </div>
        <div className="text-[11px] text-slate-500 mt-3 italic">
          Phase 1 pushes the core records above. Documents + Flex Assets + Attachments ship in Phase 2 once you&apos;ve validated the core push.
        </div>
      </Card>

      {/* Flex asset breakdown */}
      {p?.flex_assets && Object.keys(p.flex_assets).length > 0 && (
        <Card className="p-4" data-testid="itg-flex-panel">
          <div className="text-xs font-semibold uppercase text-slate-600 mb-2">Flexible Asset types found (Phase 2)</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
            {Object.entries(p.flex_assets).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between bg-slate-50 rounded px-2 py-1">
                <span className="text-slate-700 font-medium capitalize">{k.replace(/-/g, " ")}</span>
                <span className="text-slate-500">{v.count} rec · {v.field_names?.length || 0} fields</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Warnings */}
      {(p?.warning_count || 0) > 0 && (
        <Card className="p-4 bg-amber-50 border-amber-200" data-testid="itg-warnings-panel">
          <div className="flex items-start gap-2">
            <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={16}/>
            <div className="text-xs text-amber-900 flex-1">
              <div className="font-semibold">{p.warning_count} parse warning(s)</div>
              <ul className="mt-1 space-y-0.5">
                {p.warnings.map((w, i) => <li key={i}>· {w}</li>)}
              </ul>
            </div>
          </div>
        </Card>
      )}

      {/* Actions */}
      <Card className="p-5 flex items-center justify-between gap-3" data-testid="itg-actions-bar">
        <div className="text-sm text-slate-600">
          <div className="font-semibold text-slate-800">Ready to push.</div>
          <div className="text-xs mt-0.5">Run a dry-run first — it validates every payload without writing to ITGlue.</div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onDryRun} data-testid="itg-dryrun-btn">
            <PlayCircle size={14} className="mr-1.5"/>Dry-run
          </Button>
          <Button className="bg-brand-primary hover:bg-brand-primary/90" onClick={onLivePush} data-testid="itg-livepush-btn">
            <Zap size={14} className="mr-1.5"/>Push to ITGlue
          </Button>
        </div>
      </Card>
    </div>
  );
}

function CoreCard({ icon: Icon, label, count, sub, tint, testid }) {
  const tints = {
    violet: "bg-violet-50 border-violet-200 text-violet-800",
    sky:    "bg-sky-50 border-sky-200 text-sky-800",
    emerald:"bg-emerald-50 border-emerald-200 text-emerald-800",
    amber:  "bg-amber-50 border-amber-200 text-amber-800",
    rose:   "bg-rose-50 border-rose-200 text-rose-800",
    slate:  "bg-slate-50 border-slate-200 text-slate-800",
  };
  return (
    <div className={`p-3 rounded border ${tints[tint] || tints.slate}`} data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs font-medium">
        <Icon size={14}/>
        {label}
      </div>
      <div className="text-2xl font-bold mt-1">{count ?? 0}</div>
      {sub && <div className="text-[10px] opacity-70">{sub}</div>}
    </div>
  );
}

function FlexTypeCard({ entry, overrides, onOverrideChange }) {
  const [expanded, setExpanded] = useState(false);
  const exists = entry.exists_on_target;
  const [mapped, total] = entry.coverage || [0, 0];
  const amb = entry.ambiguous_columns?.length || 0;
  const unmapped = entry.unmapped_columns?.length || 0;
  const rowCount = entry.csv_active_count || 0;
  return (
    <div className={`border rounded-lg overflow-hidden ${exists ? "border-emerald-200 bg-emerald-50/40" : "border-amber-300 bg-amber-50/60"}`} data-testid={`itg-flex-card-${entry.kind}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-3 py-2 hover:bg-white/40"
      >
        <div className="flex items-center gap-2 min-w-0">
          <span className={`text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${exists ? "bg-emerald-200 text-emerald-900" : "bg-amber-200 text-amber-900"}`}>
            {exists ? "MATCHED" : "MISSING"}
          </span>
          <span className="text-sm font-semibold text-slate-800 capitalize">{entry.kind.replace(/-/g, " ")}</span>
          <span className="text-xs text-slate-500 truncate">
            {exists ? `→ ${entry.target_type_name}` : `→ needs auto-create`}
          </span>
        </div>
        <div className="flex items-center gap-3 text-[11px] shrink-0">
          <span className="text-slate-600">{rowCount} row{rowCount !== 1 ? "s" : ""}</span>
          {exists && total > 0 && (
            <span className="text-slate-700">
              <span className="text-emerald-700 font-semibold">{mapped}</span>/{total} mapped
              {amb > 0 && <span className="text-amber-700 ml-1">· {amb} ambig</span>}
              {unmapped > 0 && <span className="text-rose-700 ml-1">· {unmapped} unmapped</span>}
            </span>
          )}
        </div>
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-white/60 bg-white/60">
          {!exists && (
            <div className="text-xs text-amber-900 py-2 italic">
              This type doesn&apos;t exist on your target tenant yet. Enable &quot;Auto-create missing flex-asset types&quot; below to have it created automatically with {entry.mappings?.length || 0} field(s), or create it manually in ITGlue first and re-run.
            </div>
          )}
          {exists && (entry.mappings || []).length > 0 && (
            <div className="mt-1 space-y-1">
              {entry.mappings.map((m) => (
                <div key={m.csv_column} className="flex items-center gap-2 text-xs" data-testid={`itg-flex-row-${entry.kind}-${m.csv_column}`}>
                  <span className="w-2/5 truncate font-mono text-slate-700">{m.csv_column}</span>
                  <ArrowRight size={11} className="text-slate-400 shrink-0"/>
                  {m.match_type === "ambiguous" ? (
                    <Select
                      value={overrides[m.csv_column] || ""}
                      onValueChange={(v) => onOverrideChange(m.csv_column, v || null)}
                    >
                      <SelectTrigger className="h-7 text-xs w-2/5">
                        <SelectValue placeholder="Pick a field…"/>
                      </SelectTrigger>
                      <SelectContent>
                        {(m.candidates || []).map((c) => (
                          <SelectItem key={c.field_key} value={c.field_key}>{c.name} <span className="text-slate-400 text-[10px]">({c.field_key})</span></SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <span className={`w-2/5 truncate font-mono ${m.match_type === "unmapped" ? "text-rose-600" : m.match_type === "slug" ? "text-emerald-700" : "text-sky-700"}`}>
                      {m.field_key || "—"}
                    </span>
                  )}
                  <span className={`text-[9px] uppercase tracking-wide font-semibold ${
                    m.match_type === "slug" ? "text-emerald-700"
                    : m.match_type === "fuzzy" ? "text-sky-700"
                    : m.match_type === "ambiguous" ? "text-amber-700"
                    : m.match_type === "unmapped" ? "text-rose-600"
                    : "text-slate-500"
                  }`}>{m.match_type}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ProgressPanel({ record, progress, onReset, onOpenFlexMapping, onReify, reifying }) {
  const p = progress || {};
  const result = record?.result;
  const done = record?.status === "complete" || record?.status === "error";
  const pct = p.total > 0 ? Math.round(100 * p.current / p.total) : 0;

  // 9-stage timeline covers both Phase 1 (6 stages) + Phase 2 (3 stages).
  const STAGES = ["organizations", "locations", "contacts", "configurations", "passwords", "domains", "documents", "attachments", "flex_assets"];
  const isPhase2Stage = ["documents", "attachments", "flex_assets"].includes(p.stage);
  const stageIdx = STAGES.indexOf(p.stage);
  // Phase 1 fully complete? true after a live-push (not dry-run) if org_id is real (not "dry-run-*")
  const phase1LiveDone = done && result && result.dry_run === false && result.org_id && !String(result.org_id).startsWith("dry-");
  const phase2Done = done && !!record?.phase2_completed;
  const reified = !!record?.reified_onboarding_id;

  return (
    <div className="space-y-4" data-testid="itg-progress-panel">
      <Card className="p-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs text-slate-500 uppercase tracking-wide">{result?.dry_run ? "Dry-run" : "Live push"} — {record.org_name}</div>
            <div className="text-lg font-semibold text-slate-800 mt-0.5 flex items-center gap-2">
              {done ? (
                record.status === "error"
                  ? (<><AlertTriangle className="text-rose-600" size={18}/>Failed</>)
                  : (<><CheckCircle2 className="text-emerald-600" size={18}/>Complete</>)
              ) : (
                <><Loader2 className="animate-spin text-brand-primary" size={18}/>{p.stage || "starting"}</>
              )}
            </div>
          </div>
          {done && (
            <Button variant="outline" size="sm" onClick={onReset} data-testid="itg-progress-back">Import another</Button>
          )}
        </div>

        {/* Stage timeline */}
        <div className="mt-4 flex items-center gap-1 text-[10px]" data-testid="itg-stage-timeline">
          {STAGES.map((s, i) => {
            const isActive = i === stageIdx;
            const isDone = done || i < stageIdx;
            return (
              <div key={s} className="flex-1">
                <div className={`h-1 rounded ${isDone ? "bg-emerald-500" : isActive ? "bg-brand-primary" : "bg-slate-200"}`}/>
                <div className={`mt-1 uppercase tracking-wide ${isActive ? "font-semibold text-brand-primary" : isDone ? "text-emerald-700" : "text-slate-400"}`}>{s}</div>
              </div>
            );
          })}
        </div>

        {/* Current bar */}
        {!done && (
          <div className="mt-4">
            <div className="flex justify-between text-xs text-slate-600">
              <span data-testid="itg-progress-message">{p.message || "Working…"}</span>
              <span data-testid="itg-progress-counter">{p.current} / {p.total}</span>
            </div>
            <div className="mt-1 h-1.5 bg-slate-100 rounded overflow-hidden">
              <div className="bg-brand-primary h-full transition-all" style={{ width: `${pct}%` }}/>
            </div>
          </div>
        )}
      </Card>

      {/* Final result panel */}
      {done && result && (
        <Card className="p-4" data-testid="itg-result-panel">
          <div className="text-xs font-semibold uppercase text-slate-600 mb-2">Results</div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
            {Object.entries(result.stages || {}).map(([stage, s]) => (
              <div key={stage} className="bg-slate-50 rounded p-2 text-xs" data-testid={`itg-result-${stage}`}>
                <div className="text-slate-500 uppercase text-[9px] tracking-wide">{stage}</div>
                <div className="text-slate-800 font-semibold mt-0.5">
                  {s.created ? <span className="text-emerald-700">+{s.created} </span> : null}
                  {s.updated ? <span className="text-sky-700">~{s.updated} </span> : null}
                  {s.error ? <span className="text-rose-700">!{s.error} </span> : null}
                  {s.skipped ? <span className="text-slate-500 text-[11px]">·{s.skipped} skip</span> : null}
                </div>
              </div>
            ))}
          </div>
          {result.error && (
            <div className="mt-3 p-2 bg-rose-50 border border-rose-200 rounded text-xs text-rose-900" data-testid="itg-result-error">
              <span className="font-semibold">Error:</span> {result.error}
            </div>
          )}
          {result.log_tail && result.log_tail.length > 0 && (
            <div className="mt-3">
              <div className="text-xs text-slate-500 mb-1">Log tail:</div>
              <pre className="text-[10px] font-mono bg-slate-900 text-slate-100 p-3 rounded max-h-64 overflow-auto whitespace-pre-wrap" data-testid="itg-result-log">
                {result.log_tail.join("\n")}
              </pre>
            </div>
          )}
        </Card>
      )}

      {/* ===== Phase 2 continuation panel — appears after a successful live Phase 1 ===== */}
      {phase1LiveDone && !phase2Done && !result?.error && (
        <Card className="p-5 bg-gradient-to-br from-brand-primary/5 to-transparent border-brand-primary/30" data-testid="itg-phase2-panel">
          <div className="flex items-start gap-3">
            <ArrowRight className="text-brand-primary shrink-0 mt-1" size={20}/>
            <div className="flex-1">
              <div className="text-xs uppercase tracking-wide text-brand-primary font-semibold">Ready for Phase 2</div>
              <div className="text-lg font-bold text-slate-800 mt-0.5">Continue with Documents, Attachments &amp; Flexible Assets</div>
              <div className="text-sm text-slate-600 mt-1">
                Phase 1 completed — the target organization is live. Now push the 62 documents (with their HTML bodies), 21 attachments, and 17 flex-asset types.
              </div>
              <div className="mt-3 flex gap-2">
                <Button variant="outline" size="sm" onClick={onOpenFlexMapping} data-testid="itg-phase2-mapping-btn">
                  <Puzzle size={14} className="mr-1.5"/>Review flex-asset mapping
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* ===== Reify onboarding panel — appears after a full completion, unless already reified ===== */}
      {phase1LiveDone && !reified && !result?.error && (
        <Card className="p-5" data-testid="itg-reify-panel">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3 flex-1">
              <UserPlus className="text-brand-primary shrink-0 mt-1" size={20}/>
              <div>
                <div className="text-lg font-bold text-slate-800">Reify as onboarding</div>
                <div className="text-sm text-slate-600 mt-1">
                  Create a Sentry Cloud IT onboarding record for <span className="font-semibold">{result.org_name}</span> so it shows up on your dashboard. Core fields (org name, primary address, primary contact) are pre-filled from the CSVs. Client-facing reminders are paused by default.
                </div>
              </div>
            </div>
            <Button className="bg-brand-primary hover:bg-brand-primary/90 shrink-0" onClick={onReify} disabled={reifying} data-testid="itg-reify-btn">
              {reifying ? <Loader2 className="animate-spin mr-2" size={14}/> : <LinkIcon size={14} className="mr-2"/>}
              Reify
            </Button>
          </div>
        </Card>
      )}

      {reified && (
        <Card className="p-4 bg-emerald-50 border-emerald-200" data-testid="itg-reified-banner">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="text-emerald-600" size={18}/>
            <div className="text-sm text-emerald-900">
              Reified as onboarding <a href={`/admin/onboarding/${record.reified_onboarding_id}`} className="font-mono underline">{record.reified_onboarding_id}</a> — visible on <a href="/admin" className="underline">Admin dashboard</a>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
